import { SearchPayload } from "@/domain/common.interface";
import {
  ProgramPayload,
  Program,
  ProgramsResponse,
} from "@/domain/programs.interface";
import { axios, transformAxiosError } from "@/utils/axios.create";

export enum GetIncludes {
  orgUnits = "orgUnits",
}

const getPrograms = async ({
  sortType,
}: {
  sortType?: string;
}): Promise<Program[]> => {
  return axios
    .get("/programs", { params: { status: "ACTIVE" } })
    .then((res) => {
      if (sortType === "desc") {
        return res.data.body.reverse();
      }

      return res.data.body;
    })
    .catch((err) => {
      throw new Error(
        err.response?.data?.message || "Failed to fetch programs",
      );
    });
};

const findAll = async (): Promise<ProgramsResponse> => {
  return axios
    .get("/programs")
    .then((res) => {
      return res.data.body;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const search = async ({
  search,
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
}: SearchPayload): Promise<ProgramsResponse> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    search,
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  return axios
    .get(`/programs/search?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        programs: body.programs,
        totalCount: body.totalCount,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (data: ProgramPayload) => {
  return axios
    .post("/programs", data)
    .then((res) => res.data.body)
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({ id, data }: { id: string; data: ProgramPayload }) => {
  return axios
    .put(`/programs/${id}`, data)
    .then((res) => res.data.body)
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const softDelete = async (id: string) => {
  return axios
    .delete(`/programs/${id}`)
    .then((res) => res.data.body)
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findById = async (
  id: string,
  includes: GetIncludes[] = [],
): Promise<Program> => {
  const queryParams = new URLSearchParams({
    includes: includes.join(","),
  });
  return axios
    .get(`/programs/${id}?${queryParams.toString()}`)
    .then((res) => {
      return res.data.body as Program;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const getBudget = async (
  programId: string,
): Promise<{
  budgetLimit: number | null;
  totalSpent: number;
  remainingBudget: number | null;
  hasBudgetLimit: boolean;
}> => {
  return axios
    .get(`/programs/${programId}/budget`)
    .then((res) => {
      return res.data.body;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const ProgramService = {
  getPrograms,
  findAll,
  create,
  update,
  softDelete,
  findById,
  search,
  getBudget,
};
